import { AuthService } from './../../core/services/auth.service';
import { MenuService } from './../services/menu.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Observable, Subject, Subscription, delay, map, takeUntil, tap } from 'rxjs';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ErrorService } from '@uw/core/services/error.service';
import { LoadingService } from '@uw/core/interceptors/loader-spinner.service';
import { Router, RouterOutlet, RouterLink } from '@angular/router';
import { Roles } from '@uw/core/enums/role.enum';
import { UnitedWay } from '@uw/core/models/unitedway.model';
import { ApplicationPermissions } from '@uw/core/application-permission';
import { environment } from '@uw-env/environment';
import { MetadataService } from '@uw/core/services/metadata.service';
import { UWAlert } from '@uw/shared/models/alert.model';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSidenavContainer, MatSidenav } from '@angular/material/sidenav';
import { MatIconButton, MatButton } from '@angular/material/button';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { PermissionDirective } from '../../shared/directives/permission.directive';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { AlertComponent } from '../../shared/content/crud/alert/alert.component';
import { SubheaderComponent } from '../subheader/subheader.component';

@Component({
    selector: 'app-base',
    templateUrl: './theme-base.component.html',
    styleUrls: ['./theme-base.component.scss'],
    imports: [MatProgressSpinner, MatSidenavContainer, MatSidenav, MatIconButton, SidebarComponent, RouterOutlet, PermissionDirective, RouterLink, MatButton, MatMenuTrigger, MatMenu, NgClass, MatIcon, AlertComponent, SubheaderComponent, AsyncPipe, TranslateModule]
})
export class ThemeBaseComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild("drawer")
  mainSidebarMenu: any;

  openSidebar = false;
  openSideMenu = false;

  siteType: string = '';

  menuConfig: any;
  error$: Observable<string | null>;
  isSuperAdmin$!: Observable<boolean>;
  selectedUnitedWayName: string | undefined = '';
  unitedWayList$: Observable<UnitedWay[]> = this.authService.getUnitedWayList()
    .pipe(tap(list => this.selectedUnitedWayName = list.find(c => c.unitedWayId == this.authService.getUser()?.unitedWayId)?.name));
  permissionEnum = ApplicationPermissions;

  private unsubscribe: Subscription[] = [];
  loading: boolean = false;
  destroy$ = new Subject();

  alerts!: UWAlert[];
  version = environment.build;

   informationSecurityFrameworkPolicyFileUrl = `${environment.fileDownloadBaseUrl}/InformationSecurityPolicyFramework.pdf?t=${Date.now()}`;

  constructor(
    public errorService: ErrorService,
    private router: Router,
    public authService: AuthService,
    private translateService: TranslateService,
    private menuService: MenuService,
    private _loading: LoadingService,
    private _metaDataService: MetadataService
  ) {
    this.isSuperAdmin$ = this.authService.getUserState().pipe(map(_ => _?.roleCode === Roles.SystemAdministrator));
    this.error$ = this.errorService.getError().pipe(
      delay(0),
      map((err) => (err ? translateService.instant(err) : null))
    );
  }

  ngOnInit(): void {
    this.siteType = environment.mode;
    this.listenToLoading();
    this._metaDataService.getNotifications().pipe(
      map(alerts => {
        this.alerts = alerts;
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  ngAfterViewInit() {
    this.menuService.donarSubMenuClick().subscribe(toggle => {
      if (toggle && this.mainSidebarMenu.closed) {
        this.mainSidebarMenu.open();
      }
    });

    this.menuService.menuLinkClicked().subscribe(toggle => {
      if (toggle && this.mainSidebarMenu.open) {
        this.mainSidebarMenu.close();
      }
    });

  }

  onAlertClose(alertIndex: number) {
    this.alerts.splice(alertIndex);
  }

  switchToDonationSite() {
    let token = localStorage.getItem('token');

    //reset the language toggle used flag as the user is leaving the admin site.
    localStorage.removeItem('langToggleUsed');

    let donationSiteUrl = environment.donorSiteUrl;

    if (token) {
      donationSiteUrl +='?access=switch&lang=' + localStorage.getItem('lang');
    }

    window.location.href = donationSiteUrl;
  }

  onSideMenuClick() {
    this.router.navigate(['/private', { outlets: { sidebar: ['landing'] } }]);
  }

  onSidebarClose() {
    this.router.navigate(['/private', { outlets: { sidebar: null } }]);
  }

  listenToLoading(): void {
    this._loading.loadingSub
      .pipe(delay(0)) // This prevents a ExpressionChangedAfterItHasBeenCheckedError for subsequent requests
      .subscribe((loading) => {
        this.loading = loading;
      });
  }

  onDismiss() {
    this.errorService.resetError();
  }

  changeLang(e: Event) {
    e.preventDefault();
    const curLang = this.translateService.currentLang;
    localStorage.setItem('lang', curLang === 'en' ? 'fr' : 'en');
    localStorage.setItem('langToggleUsed', 'true');
    window.location.reload();
  }

  switchContent(unitedWayId: number) {
    this.authService.switchContext(unitedWayId).subscribe(res => {
      window.location.reload();
    });
  }

  public onKeydown(event: any, unitedWayId: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.switchContent(unitedWayId);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
